// flushSync intentionally removed — see handleNavClick below.
import { AppSwitcherTitle } from "@/components/tacticLink/AppSwitcherTitle";
import { LayoutDashboard, Calendar, Droplets, FileText, Bug, BarChart3, Sparkles, Package, Settings, LogOut, Users, ShoppingBag, UserCircle } from "lucide-react";
import { GreenhouseIcon } from "@/components/icons/GreenhouseIcon";
import companyLogo from "@/assets/ulture-logo-transparent.png";
import { useTranslation } from "react-i18next";
import { NavLink } from "@/components/NavLink";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { NotificationDropdown, NotificationToast } from "@/components/notifications";
import { useNotifications } from "@/hooks/useNotifications";
import { OnboardingStepsNav } from "@/components/onboarding/OnboardingStepsNav";
import { useHortalandEnabled } from "@/hooks/useHortalandEnabled";
import { useManagerPermissions, MANAGEABLE_SECTIONS } from "@/hooks/useManagerPermissions";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useOnboardingGate } from "@/components/routing/OnboardingGate";

// Maps each onboarding step_key to the routes it unlocks
const STEP_UNLOCKS: Record<string, string[]> = {
  structure: ['/blocks'],
  pests: ['/pests'],
  inventory: ['/inventory'],
  treatments: ['/analytics', '/spray-logs'],
  planner: ['/planner', '/advanced'],
  team: ['/teams'],
};

// Routes always visible regardless of onboarding progress
const ALWAYS_VISIBLE_ROUTES = ['/dashboard', '/settings', '/shop'];

// When these 4 steps are done, unlock the entire menu
const EARLY_UNLOCK_STEPS = ['structure', 'pests', 'inventory', 'treatments'];

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  tooltip?: string;
  /**
   * Solo en escritorio. Por debajo de `lg` esta barra NO desaparece: se
   * convierte en cajón (ver `Sidebar` → Sheet cuando `isMobile`), así que sin
   * esta marca la fila también saldría en el menú del teléfono y de la tableta.
   */
  desktopOnly?: boolean;
}

interface MenuGroup {
  /** Section heading; omit for the top "General" cluster which renders label-less. */
  label?: string;
  items: MenuItem[];
}

export function AppSidebar() {
  const { t } = useTranslation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const { company, role, signOut } = useAuth();
  const { latestNotification, clearLatestNotification } = useNotifications();
  const { enabled: hortalandEnabled, loading: hortalandLoading } = useHortalandEnabled();
  const { isSectionVisible, loading: permissionsLoading } = useManagerPermissions();
  const { completedSteps, isComplete: guideComplete, isLoading: guideLoading } = useOnboarding();
  const { active: onboardingActive } = useOnboardingGate();
  const isCollapsed = state === "collapsed";

  const handleNavClick = () => {
    if (!isMobile) return;
    // Let React batch the close with the navigation. Forcing a synchronous
    // close (flushSync) made the sheet's exit animation collide with the
    // new page's first paint, producing a "flashy" feel on every nav.
    setOpenMobile(false);
  };

  // Grouped into labelled clusters so the nav reads as ~4 areas instead of a
  // long undifferentiated stack. Configuración is pulled out into the pinned
  // footer zone (below) alongside Help + Sign out.
  const menuGroups: MenuGroup[] = [
    {
      items: [
        { title: t("nav.dashboard"), url: "/dashboard", icon: LayoutDashboard },
        { title: t("nav.blockMap"), url: "/blocks", icon: GreenhouseIcon },
      ],
    },
    {
      label: t("nav.groups.operations", "Operación"),
      items: [
        { title: t("nav.pests"), url: "/pests", icon: Bug },
        // Campañas ya no vive en el menú: es una subpágina de Ajustes, entre
        // Estructura y Equipo (2026-08-11). Se configura una vez por temporada,
        // no es una pantalla de trabajo diario.
        { title: t("nav.planner"), url: "/planner", icon: Calendar },
        { title: t("nav.sprayLogsShort", "Registros"), tooltip: t("nav.sprayLogs"), url: "/spray-logs", icon: Droplets },
      ],
    },
    {
      label: t("nav.groups.resources", "Recursos"),
      items: [
        { title: t("nav.inventory"), url: "/inventory", icon: Package },
        { title: t("nav.shop", "Shop"), url: "/shop", icon: ShoppingBag },
        { title: t("nav.team"), url: "/teams", icon: Users },
      ],
    },
    {
      label: t("nav.groups.intelligence", "Inteligencia"),
      items: [
        { title: t("nav.analyticsShort", "Analíticas"), tooltip: t("nav.analyticsReports"), url: "/analytics", icon: BarChart3, adminOnly: true },
        // Ulture Assistant vuelve a ser una subpágina, pero SOLO en escritorio: ahí
        // hay sitio para el chat a página completa y la pestaña «Asistente» sale de
        // la ventana del micrófono. En teléfono y tableta sigue viviendo en esa
        // ventana y por eso la fila lleva `desktopOnly`.
        { title: t("nav.advancedFeatures"), url: "/advanced", icon: Sparkles, adminOnly: true, desktopOnly: true },
      ],
    },
  ];

  // Always visible for every role (not in MANAGEABLE_SECTIONS, not adminOnly),
  // so it renders statically in the footer zone.
  /* Durante la puesta en marcha, Ajustes no lleva a ninguna parte: la puerta
     bloquea /settings entero, así que el botón se pulsaba y no pasaba nada. Se
     convierte en Perfil y apunta a Mi Cuenta —idioma, facturación, avisos y
     legal—, que es justo lo que la puerta SÍ deja abierto. Es el mismo destino
     al que llega el teléfono tocando el avatar. */
  const settingsItem: MenuItem = onboardingActive
    ? { title: t("nav.profile", "Perfil"), url: "/settings/my-account", icon: UserCircle }
    : { title: t("nav.settings"), url: "/settings", icon: Settings };

  // Check if a route is unlocked by onboarding progress
  const isRouteUnlocked = (url: string): boolean => {
    /* EL DESBLOQUEO PROGRESIVO ES DE LA PUESTA EN MARCHA, Y SOLO LA VE QUIEN LA
       ESTÁ HACIENDO (usuario 24-09-2026). `completedSteps` es de CADA usuario, y
       a un encargado invitado a una empresa ya montada le vale cero: nunca hará
       la guía, así que su menú se quedaba en lo poco que abriera un paso suyo
       suelto. El caso real: Emilio, con Registros, Inventario y Analítica
       concedidos, solo veía Inventario — `sprayLogs` y `analytics` los abre el
       paso «treatments», que él no tiene ni va a tener.

       Y no se veía desde la cuenta del admin: él tiene todos los pasos, así que
       para quien reparte los permisos el menú se ve entero. La puerta de la
       guía (`useOnboardingGate`) ya era solo del primer admin; esto no llevaba
       esa comprobación. Fuera de la puesta en marcha mandan el rol y los
       permisos, y nada más. */
    if (!onboardingActive) return true;
    // Always visible routes
    if (ALWAYS_VISIBLE_ROUTES.includes(url)) return true;
    // If guide is complete or still loading, show everything
    if (guideComplete || guideLoading) return true;
    // If first 4 steps are done, unlock everything
    if (EARLY_UNLOCK_STEPS.every(s => completedSteps.includes(s))) return true;
    // Find which step unlocks this route
    for (const [stepKey, routes] of Object.entries(STEP_UNLOCKS)) {
      if (routes.includes(url)) {
        return completedSteps.includes(stepKey);
      }
    }
    // Routes not in the map are visible by default
    return true;
  };

  // Routes hidden from the sidebar while the onboarding gate is active.
  // Even if reached, OnboardingGate would redirect — but we hide them
  // visually so users see only the routes that matter during setup.
  const ONBOARDING_HIDDEN = new Set([
    "/dashboard",
    "/spray-logs",
    "/blocks",
    "/analytics",
    "/advanced",
    "/shop",
  ]);

  const isItemVisible = (item: MenuItem): boolean => {
    // 0. Onboarding gate: hide non-essential routes during initial setup
    if (onboardingActive && ONBOARDING_HIDDEN.has(item.url)) {
      return false;
    }

    // 0.5. Filas de solo escritorio: por debajo de `lg` esta barra es el cajón
    // del teléfono/tableta, así que hay que quitarlas a mano.
    if (item.desktopOnly && isMobile) {
      return false;
    }

    // 1. Progressive unlock: hide items whose onboarding step isn't done yet
    if (!isRouteUnlocked(item.url)) {
      return false;
    }

    // 2. Check manager section permissions
    if (role === 'manager') {
      const section = MANAGEABLE_SECTIONS.find(s => item.url.startsWith(s.route));
      if (section && !isSectionVisible(section.key)) {
        return false;
      }
    }

    // 3. Then check adminOnly (only admin + manager can see these)
    if (item.adminOnly) {
      return role === 'admin' || role === 'manager';
    }

    // 4. Shop visibility
    if (item.url === '/shop' && (hortalandLoading || !hortalandEnabled)) {
      return false;
    }

    return true;
  };

  // Filter each group's items, then drop groups left empty so no orphan labels.
  const visibleGroups = menuGroups
    .map(group => ({ ...group, items: group.items.filter(isItemVisible) }))
    .filter(group => group.items.length > 0);

  // Hide the section headings during onboarding: the list is short and guided
  // then, so the flat look reads cleaner than single-item labelled clusters.
  const showGroupLabels = !onboardingActive;

  // Shared row renderer for a single nav item. Compact heights/fonts so the
  // whole menu fits a laptop viewport without scrolling.
  const renderItem = (item: MenuItem) => (
    <SidebarMenuItem
      key={item.url}
      className="animate-in fade-in slide-in-from-left-2 duration-300"
    >
      <SidebarMenuButton asChild tooltip={item.tooltip ?? item.title} className={cn("hover:!text-white", isCollapsed ? "!h-[44px] !w-[44px] !p-0 [&>svg]:!w-5 [&>svg]:!h-5 2xl:!h-[52px] 2xl:!w-[52px] 2xl:[&>svg]:!w-6 2xl:[&>svg]:!h-6" : "!h-[34px] [&>svg]:!w-[18px] [&>svg]:!h-[18px] 2xl:!h-[40px] 2xl:[&>svg]:!w-[22px] 2xl:[&>svg]:!h-[22px]")}>
        <NavLink
          to={item.url}
          className={cn(
            "flex items-center rounded-[11px] transition-all duration-150 text-white font-['Poppins'] hover:bg-white/[0.12] hover:!text-white",
            isCollapsed
              ? "justify-center font-bold"
              : "gap-[11px] px-[13px] text-[13px] font-bold md:text-[13.5px] md:font-semibold 2xl:text-[15px] 2xl:gap-[13px] 2xl:px-[15px]"
          )}
          activeClassName="ipm-pill-active !text-white"
          onClick={handleNavClick}
        >
          <item.icon />
          {!isCollapsed && <span className="flex-1 truncate">{item.title}</span>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  return (
    <Sidebar variant="floating" collapsible="icon" className="app-sidebar-gradient border-none md:z-30">
      <SidebarHeader className={cn("md:border-b md:border-white/15 relative z-10", isCollapsed ? "p-2" : "p-4")}>
        <div className={cn("flex items-center gap-3 relative", isCollapsed && "justify-center")}>
          {/* Logo in a white rounded square chip (reference: 36px, radius 11) */}
          <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 2xl:w-11 2xl:h-11 rounded-[11px] bg-white shadow-[0_3px_10px_rgba(8,30,45,0.18)]">
            <img src={companyLogo} alt="Ulture Pest" className="object-contain w-[30px] h-[30px] 2xl:w-[36px] 2xl:h-[36px]" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0 pr-12">
              {/* Con Ulture Tactic conectada, pulsar aquí cambia de app (30-10-2026). */}
              <AppSwitcherTitle>
                <h2 className="font-['Poppins'] font-bold text-base 2xl:text-lg text-white leading-tight">{t("auth.platformTitle")}</h2>
                {company && <p className="font-['Mulish'] font-bold text-[11.5px] 2xl:text-[13px] text-white/[0.82] truncate">{company.name}</p>}
              </AppSwitcherTitle>
            </div>
          )}
          {/* Notification bell — floats above the title so it never clips long names */}
          {!isCollapsed && (
            <div className="absolute top-0 right-0 z-20 [&_svg]:text-white [&_button]:bg-white/10 [&_button:hover]:bg-white/25 [&_button]:rounded-xl">
              <NotificationDropdown collapsed={isCollapsed} />
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="opacity-100 relative z-10 flex flex-col overflow-hidden">
        {/* Scrollable, grouped main menu */}
        <div className="flex-1 min-h-0 overflow-y-auto pt-2">
          {onboardingActive ? (
            /* Durante la puesta en marcha el menú ES la guía: los seis pasos en
               orden, con candado en los que aún no tocan. El resto de páginas no
               se listan porque la puerta no dejaría entrar de todas formas. */
            <OnboardingStepsNav collapsed={isCollapsed} onNavigate={handleNavClick} />
          ) : (role === 'manager' && permissionsLoading) ? (
            <div className="space-y-1 px-4 pt-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-10 rounded-xl bg-white/15 animate-pulse" />
              ))}
            </div>
          ) : visibleGroups.map((group, gi) => (
            <SidebarGroup key={group.label ?? `general-${gi}`} className={cn("px-2", gi === 0 ? "pt-1 pb-0.5" : "pt-0.5 pb-0.5")}>
              {showGroupLabels && group.label && !isCollapsed && (
                <SidebarGroupLabel className="h-[18px] 2xl:h-[22px] px-[13px] mb-px font-['Poppins'] font-semibold uppercase tracking-[0.1em] text-[9.5px] 2xl:text-[11px] text-white/50">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className={cn("space-y-0.5", isCollapsed && "space-y-2 items-center")}>
                  {group.items.map(renderItem)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </div>

        {/* Footer items pinned to the bottom — above a hairline (reference) */}
        <SidebarGroup className="mt-auto pb-3 pt-2 border-t border-white/[0.16]">
          <SidebarGroupContent>
            <SidebarMenu className={cn("space-y-0.5", isCollapsed && "space-y-2.5 items-center")}>
              {/* Configuración — moved down from the main list into the pinned footer */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={settingsItem.title} className={cn("hover:!text-white", isCollapsed ? "!h-[44px] !w-[44px] !p-0 [&>svg]:!w-5 [&>svg]:!h-5 2xl:!h-[52px] 2xl:!w-[52px] 2xl:[&>svg]:!w-6 2xl:[&>svg]:!h-6" : "!h-[34px] [&>svg]:!w-[18px] [&>svg]:!h-[18px] 2xl:!h-[40px] 2xl:[&>svg]:!w-[22px] 2xl:[&>svg]:!h-[22px]")}>
                  <NavLink
                    to={settingsItem.url}
                    className={cn(
                      "flex items-center rounded-[11px] transition-all duration-150 text-white font-['Poppins'] hover:bg-white/[0.12] hover:!text-white",
                      isCollapsed ? "justify-center font-bold" : "gap-[11px] px-[13px] text-[13px] font-bold md:text-[13.5px] md:font-semibold 2xl:text-[15px] 2xl:gap-[13px] 2xl:px-[15px]"
                    )}
                    activeClassName="ipm-pill-active !text-white"
                    onClick={handleNavClick}
                  >
                    <settingsItem.icon />
                    {!isCollapsed && <span>{settingsItem.title}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Centro de Ayuda ya no vive aquí: está dentro de Ajustes, igual que en el
                  móvil. Un solo sitio donde buscarlo, en los dos tamaños. */}

              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t("auth.signOut")} className={cn("hover:!text-white", isCollapsed ? "!h-[44px] !w-[44px] !p-0 [&>svg]:!w-5 [&>svg]:!h-5 2xl:!h-[52px] 2xl:!w-[52px] 2xl:[&>svg]:!w-6 2xl:[&>svg]:!h-6" : "!h-[34px] [&>svg]:!w-[18px] [&>svg]:!h-[18px] 2xl:!h-[40px] 2xl:[&>svg]:!w-[22px] 2xl:[&>svg]:!h-[22px]")}>
                  <button
                    onClick={signOut}
                    className={cn(
                      "flex items-center rounded-[11px] transition-all duration-150 font-['Poppins'] text-white/90 hover:bg-white/[0.12] hover:!text-white w-full text-left",
                      isCollapsed ? "justify-center font-bold" : "gap-[11px] px-[13px] text-[13px] font-bold md:text-[13.5px] md:font-semibold 2xl:text-[15px] 2xl:gap-[13px] 2xl:px-[15px]"
                    )}
                  >
                    <LogOut />
                    {!isCollapsed && <span>{t("auth.signOut")}</span>}
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      {/* Toast notification popup */}
      <NotificationToast 
        notification={latestNotification} 
        onDismiss={clearLatestNotification} 
      />
    </Sidebar>
  );
}
